import { useEffect, useRef, useCallback } from 'react';
import { useSettings } from './contexts/SettingsContext';
import api from './utils/api';

const THEME_STYLE_ID = 'global-navigation-override';
const HEADER_STYLE_ID = 'theme-override-styles';
const GUARD_STYLE_ID = 'guard-dashboard-theme-styles';

// This component ensures the theme is applied when the app starts
const ThemeInitializer = () => {
  const { settings, error } = useSettings();
  const initialFetchDoneRef = useRef(false);
  const apiSettingsRef = useRef(null);
  const lastNavigationStylesRef = useRef('');
  const lastHeaderStylesRef = useRef('');
  const lastGuardStylesRef = useRef('');
  
  const setStyleTag = (id, content, lastContentRef) => {
    // Only update if content has changed
    if (lastContentRef.current === content) return;
    
    let styleEl = document.getElementById(id);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = id;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = content;
    lastContentRef.current = content;
  };
  
  const directlyApplySidebarStyles = useCallback((settingsToApply) => {
    if (!settingsToApply) return;
    
    // Skip applying sidebar styles on landing page and other excluded pages
    if (typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/superadmin') || 
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/support' ||
      window.location.pathname === '/forgot-password' ||
      window.location.pathname === '/reset-password' ||
      window.location.pathname === '/landing' ||
      window.location.pathname === '/contact'
    )) return;
    
    // Apply styles directly to the DOM elements
    setTimeout(() => {
      const sidebar = document.querySelector('.sidebar-navigation');
      if (sidebar) {
        sidebar.style.backgroundColor = settingsToApply.sidebar_bg_color || '#ffffff';
        
        // Style all menu items
        const menuLinks = document.querySelectorAll('.sidebar-navigation .menu-link');
        menuLinks.forEach(link => {
          link.style.color = settingsToApply.sidebar_text_color || '#212529';
        });
        
        // Style all icons
        const menuIcons = document.querySelectorAll('.sidebar-navigation .menu-icon');
        menuIcons.forEach(icon => {
          icon.style.color = settingsToApply.sidebar_text_color || '#212529';
        });
        
        // Style active items
        const activeItems = document.querySelectorAll('.sidebar-navigation .active, .sidebar-navigation .menu-item.active');
        activeItems.forEach(item => {
          item.style.backgroundColor = settingsToApply.sidebar_active_bg_color || '#1F458E';
          
          // Style all children of active items
          const children = item.querySelectorAll('*');
          children.forEach(child => {
            child.style.color = settingsToApply.sidebar_active_text_color || '#ffffff';
          });
        });
      }
    }, 100);
  }, []);

  const applyNavigationStyles = useCallback((settingsToApply = {}) => {
    // Skip injecting global CSS variables on superadmin pages, privacy policy page, and support page
    if (typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/superadmin') || 
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/support' ||
      window.location.pathname === '/forgot-password' ||
      window.location.pathname === '/reset-password' ||
      window.location.pathname === '/landing' ||
      window.location.pathname === '/contact'
    )) { 
      return;
    }
    
    const get = (key, fallback) =>
      settingsToApply[key] || localStorage.getItem(`app_${key}`) || fallback;
    
    const primaryColor = get('theme_primary_color', '#1F458E');
    const sidebarBgColor = get('sidebar_bg_color', '#ffffff');
    const sidebarTextColor = get('sidebar_text_color', '#212529');
    const sidebarIconColor = get('sidebar_icon_color', '#212529');
    const sidebarActiveBgColor = get('sidebar_active_bg_color', primaryColor);
    const sidebarActiveTextColor = get('sidebar_active_text_color', '#ffffff');
    const sidebarHoverBgColor = get('sidebar_hover_bg_color', '#f8f9fa');
    const sidebarHoverTextColor = get('sidebar_hover_text_color', primaryColor);
    const sidebarBorderColor = get('sidebar_border_color', '#dee2e6');
    const sidebarWidth = get('sidebar_width', '250');
 
    // Style content
    const styleContent = `
      /* Theme colors applied to key elements - LET SETTINGSCONTEXT HANDLE BUTTONS */
      .text-primary,
      a.text-primary {
        color: ${primaryColor} !important;
      }
      
      /* Page titles */
      .page-title {
        color: ${primaryColor} !important;
      }
      
      /* Dashboard stat cards */
      .dashboard-stat-card {
        border-left: 4px solid ${primaryColor} !important;
      }
      
      .dashboard-stat-card .stat-value {
        color: ${primaryColor} !important;
      }
      
      /* Sidebar Navigation Styles */
      .sidebar-navigation,
      .sidebar,
      .side-nav,
      nav.sidebar,
      .sidebar-nav,
      .nav-sidebar,
      .side-navigation,
      .left-sidebar {
        background-color: ${sidebarBgColor} !important;
        border-right: 1px solid ${sidebarBorderColor} !important;
        width: ${sidebarWidth}px !important;
      }
      .sidebar-navigation.collapsed { width: 60px !important; }
      .sidebar-navigation .menu-icon,
      .sidebar-navigation .fas,
      .sidebar-navigation .far,
      .sidebar i,
      .sidebar .fas,
      .sidebar .far,
      .sidebar-item i {
        color: ${sidebarIconColor} !important;
      }
      .sidebar-navigation .menu-text,
      .sidebar-navigation .menu-link,
      .sidebar-navigation a,
      .sidebar a,
      .sidebar-item a,
      .nav-link,
      .sidebar-link {
        color: ${sidebarTextColor} !important;
        text-decoration: none !important;
      }
      /* --- Fix visited link color for sidebar --- */
      .sidebar-navigation .menu-link:visited,
      .sidebar-navigation .menu-link:visited:not(.active) {
        color: ${sidebarTextColor} !important;
        background-color: ${sidebarBgColor} !important;
        text-decoration: none !important;
      }
      .sidebar-navigation .menu-link:visited.active,
      .sidebar-navigation .menu-link.active:visited,
      .sidebar-navigation .menu-item.active .menu-link:visited {
        color: ${sidebarActiveTextColor} !important;
        background-color: ${sidebarActiveBgColor} !important;
        text-decoration: none !important;
      }
      .sidebar-navigation .menu-link:visited:hover,
      .sidebar-navigation .menu-link:hover:visited {
        color: ${sidebarHoverTextColor} !important;
        background-color: ${sidebarHoverBgColor} !important;
      }
      .sidebar-navigation .menu-item.active,
      .sidebar-navigation li.active,
      .sidebar-navigation .active,
      .sidebar .active,
      .sidebar-item.active,
      .nav-item.active,
      .nav-link.active {
        background-color: ${sidebarActiveBgColor} !important;
        color: ${sidebarActiveTextColor} !important;
        border-color: ${sidebarActiveBgColor} !important;
      }
      
      /* Add hover styles for active items to maintain the active styling */
      .sidebar-navigation .menu-item.active:hover,
      .sidebar-navigation li.active:hover,
      .sidebar-navigation .active:hover,
      .sidebar .active:hover,
      .sidebar-item.active:hover,
      .nav-item.active:hover,
      .nav-link.active:hover {
        background-color: ${sidebarActiveBgColor} !important;
        color: ${sidebarActiveTextColor} !important;
        transition: none !important;  /* Prevent any transition effects */
        border-color: ${sidebarActiveBgColor} !important;
      }
      
      /* Block any :hover pseudo-class effects for active items */
      .sidebar-navigation .menu-link.active:hover,
      .sidebar-navigation .menu-item.active > a:hover {
        background-color: ${sidebarActiveBgColor} !important;
        color: ${sidebarActiveTextColor} !important;
        transition: none !important;
      }
      
      .sidebar-navigation .menu-item.active *,
      .sidebar-navigation li.active *,
      .sidebar-navigation .active *,
      .sidebar .active *,
      .sidebar-item.active *,
      .nav-item.active *,
      .nav-link.active * {
        color: ${sidebarActiveTextColor} !important;
      }
      
      /* Add hover styles for active items' children to maintain the active styling */
      .sidebar-navigation .menu-item.active:hover *,
      .sidebar-navigation li.active:hover *,
      .sidebar-navigation .active:hover *,
      .sidebar .active:hover *,
      .sidebar-item.active:hover *,
      .nav-item.active:hover *,
      .nav-link.active:hover * {
        color: ${sidebarActiveTextColor} !important;
      }
      
      /* Hover styles for inactive items */
      .sidebar-navigation .menu-item:hover:not(.active),
      .sidebar-navigation li:hover:not(.active),
      .sidebar-navigation a:hover:not(.active),
      .sidebar a:hover:not(.active),
      .nav-link:hover:not(.active),
      .sidebar-link:hover:not(.active) {
        background-color: ${sidebarHoverBgColor} !important;
        color: ${sidebarHoverTextColor} !important;
      }
      
      .sidebar-navigation .menu-item:hover:not(.active) *,
      .sidebar-navigation li:hover:not(.active) *,
      .sidebar-item:hover:not(.active) *,
      .nav-item:hover:not(.active) * {
        color: ${sidebarHoverTextColor} !important;
      }
      
      /* Tabs Navigation Theming */
      .nav-tabs {
        border-bottom: 2px solid ${primaryColor} !important;
      }
      .nav-tabs .nav-link,
      .nav-tabs .nav-item .nav-link,
      .nav-tabs .nav-item button.nav-link {
        color: #212529 !important;
        border: none !important;
        border-radius: 6px 6px 0 0 !important;
        margin-right: 4px;
        font-weight: 500;
        transition: all 0.2s;
      }
      .nav-tabs .nav-link.active,
      .nav-tabs .nav-link.active:visited,
      .nav-tabs .nav-item .nav-link.active,
      .nav-tabs .nav-item button.nav-link.active {
        color: white !important;
        background-color: ${primaryColor} !important;
        border: none !important;
        font-weight: 600 !important;
      }
      .nav-tabs .nav-link:hover:not(.active),
      .nav-tabs .nav-item .nav-link:hover:not(.active),
      .nav-tabs .nav-item button.nav-link:hover:not(.active) {
        color: ${primaryColor} !important;
        background-color: ${primaryColor}20 !important;
      }
      
      /* Pagination */
      .pagination .page-link {
        color: ${primaryColor} !important;
      }
      
      .pagination .page-link:hover {
        color: white !important;
        background-color: ${primaryColor} !important;
        border-color: ${primaryColor} !important;
      }
      
      .pagination .page-item.active .page-link {
        color: white !important;
        background-color: ${primaryColor} !important;
        border-color: ${primaryColor} !important;
      }
    `;

    setStyleTag(THEME_STYLE_ID, styleContent, lastNavigationStylesRef);
  }, []);

  const applyHeaderStyles = useCallback((settingsToApply = {}) => {
    // Skip injecting header styles on landing page and other excluded pages
    if (typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/superadmin') || 
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/support' ||
      window.location.pathname === '/forgot-password' ||
      window.location.pathname === '/reset-password' ||
      window.location.pathname === '/landing' ||
      window.location.pathname === '/contact'
    )) return;
    
    const h1Color = settingsToApply.h1_color || localStorage.getItem('app_h1_color') || '#212529';
    const h2Color = settingsToApply.h2_color || localStorage.getItem('app_h2_color') || '#212529';
    const h3Color = settingsToApply.h3_color || localStorage.getItem('app_h3_color') || '#212529';
    const blockTitleColor = settingsToApply.block_title_color || localStorage.getItem('app_block_title_color') || '#8B4513';
    const blockTitleBgColor = settingsToApply.block_title_bg_color || localStorage.getItem('app_block_title_bg_color') || '#f8f9fa';
    
    const headerContent = `
      h1 { color: ${h1Color} !important; }
      h2 { color: ${h2Color} !important; }
      h3 { color: ${h3Color} !important; }
      
      .card-header, 
      .settings-section-header,
      .section-header,
      .block-title,
      .card-title {
        color: ${blockTitleColor} !important;
        background-color: ${blockTitleBgColor} !important;
      }
    `;
    setStyleTag(HEADER_STYLE_ID, headerContent, lastHeaderStylesRef);
  }, []);
  
  const applyGuardStyles = useCallback((settingsToApply = {}) => {
    // Skip injecting guard styles on landing page and other excluded pages
    if (typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/superadmin') || 
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/support' ||
      window.location.pathname === '/forgot-password' ||
      window.location.pathname === '/reset-password' ||
      window.location.pathname === '/landing' ||
      window.location.pathname === '/contact'
    )) return;
    
    const get = (key, fallback) =>
      settingsToApply[key] || localStorage.getItem(`app_${key}`) || fallback;
    
    const primaryColor = get('theme_primary_color', '#1F458E');
    const blockTitleColor = get('block_title_color', '#8B4513');
    const blockTitleBgColor = get('block_title_bg_color', '#f8f9fa');
    
    const guardContent = `
      :root {
        --sidebar-active-bg-color: ${get('sidebar_active_bg_color', '#1F458E')};
        --sidebar-active-text-color: ${get('sidebar_active_text_color', '#ffffff')};
        --sidebar-bg-color: ${get('sidebar_bg_color', '#ffffff')};
        --sidebar-text-color: ${get('sidebar_text_color', '#212529')};
        --sidebar-hover-bg-color: ${get('sidebar_hover_bg_color', '#f8f9fa')};
        --sidebar-hover-text-color: ${get('sidebar_hover_text_color', '#1F458E')};
        --block-title-bg-color: ${blockTitleBgColor};
        --block-title-color: ${blockTitleColor};
        --primary-color: ${primaryColor};
      }
      
      /* Card header styles for dashboard */
      .card-header, 
      .settings-section-header,
      .section-header,
      .block-title,
      .card-title {
        color: ${blockTitleColor} !important;
        background-color: ${blockTitleBgColor} !important;
      }
    `;
    setStyleTag(GUARD_STYLE_ID, guardContent, lastGuardStylesRef);
  }, []);

  // Use settings from SettingsContext first, fallback to direct API call if needed
  useEffect(() => {
    // If settings are available from context, use them
    if (settings && Object.keys(settings).length > 0) { 
      applyNavigationStyles(settings);
      applyHeaderStyles(settings);
      applyGuardStyles(settings);
      directlyApplySidebarStyles(settings);
      initialFetchDoneRef.current = true;
      return;
    }
    
    // Skip if we've already done the initial fetch
    if (initialFetchDoneRef.current) return;
    
    // Only fetch directly if no settings are available from context
    const fetchSettingsDirectly = async () => {
      try { 
        const response = await api.get('/api/settings/public'); 
        apiSettingsRef.current = response.data;
        
        // Apply these settings immediately
        if (apiSettingsRef.current) {
          applyNavigationStyles(apiSettingsRef.current);
          applyHeaderStyles(apiSettingsRef.current);
          applyGuardStyles(apiSettingsRef.current);
          directlyApplySidebarStyles(apiSettingsRef.current);
        }
        
        // Mark that we've done the initial fetch
        initialFetchDoneRef.current = true;
      } catch (err) {
        console.error('Error directly fetching settings:', err);
      }
    };
    
    fetchSettingsDirectly();
  }, [settings, applyNavigationStyles, applyHeaderStyles, applyGuardStyles, directlyApplySidebarStyles]); // Depend on settings from context

  // Apply styles on mount and when settings change
  useEffect(() => {
    applyNavigationStyles(settings);
    applyHeaderStyles(settings);
    applyGuardStyles(settings);
    
    // If there was an error loading settings from API, ensure we apply from localStorage
    if (error) {
      console.warn('Error detected in settings API, applying from localStorage:', error);
      applyNavigationStyles({});
      applyHeaderStyles({});
      applyGuardStyles({});
    }
  }, [settings, error, applyNavigationStyles, applyHeaderStyles, applyGuardStyles]);

  // Only observe for sidebar-navigation being added, not all mutations
  useEffect(() => {
    const ensureSidebarStyled = () => {
      const sidebar = document.querySelector('.sidebar-navigation');
      if (sidebar) applyNavigationStyles(settings);
    };
    ensureSidebarStyled();
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if ([...mutation.addedNodes].some(node => node.classList && node.classList.contains('sidebar-navigation'))) {
          applyNavigationStyles(settings);
          applyGuardStyles(settings);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [settings, applyNavigationStyles, applyGuardStyles]);
  
  return null; // This component doesn't render anything
};

export default ThemeInitializer;